/**
 * BulkImportModal Component
 * Modal dialog providing instructions and interface for CSV bulk user import with TAP-only authentication.
 * TAP (Temporary Access Pass) is the only authentication method for bulk-created users.
 * Contains download template link, TAP configuration, and CSV import button with clear guidance.
 */

import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Label,
  Dropdown,
  Option,
  Checkbox,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { ArrowDownload24Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { CSVBulkImportButton, type ParsedCSVData } from '../csv';

const TAP_LIFETIME_OPTIONS = [
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
  { label: '3 hours', value: 180 },
  { label: '4 hours', value: 240 },
  { label: '5 hours', value: 300 },
  { label: '6 hours', value: 360 },
  { label: '7 hours', value: 420 },
  { label: '8 hours', value: 480 },
];

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCSVParsed: (data: ParsedCSVData) => void;
  onError: (error: string) => void;
  disabled?: boolean;
}

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
  },
  instructions: {
    lineHeight: '1.6',
  },
  stepsList: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXXL,
  },
  columnList: {
    marginTop: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalXXL,
    color: tokens.colorNeutralForeground2,
  },
  downloadButton: {
    width: '250px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  importButton: {
    width: '250px',
    display: 'flex',
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    '& button': {
      width: '100%',
    },
  },
});

export function BulkImportModal({
  isOpen,
  onClose,
  onCSVParsed,
  onError,
  disabled = false,
}: BulkImportModalProps) {
  const styles = useStyles();

  // TAP configuration state
  const [tapLifetimeMinutes, setTapLifetimeMinutes] = useState(60);
  const [tapOneTimeUse, setTapOneTimeUse] = useState(true);

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/sample-users.csv';
    link.download = 'sample-users.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Wrapper to add TAP configuration to parsed CSV data
  const handleCSVParsed = (data: ParsedCSVData) => {
    // Add TAP configuration to the parsed data
    const enrichedData: ParsedCSVData = {
      ...data,
      tapConfig: {
        tap_lifetime_minutes: tapLifetimeMinutes,
        tap_one_time_use: tapOneTimeUse,
      },
    };
    onCSVParsed(enrichedData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface data-testid="bulk-import-modal">
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            }
          >
            Bulk Import Users
          </DialogTitle>
          <DialogContent>
            <div className={styles.content}>
              {/* Download Button */}
              <Button
                appearance="primary"
                icon={<ArrowDownload24Regular />}
                onClick={handleDownloadTemplate}
                className={styles.downloadButton}
                data-testid="bulk-import-download-template"
              >
                Download CSV Template
              </Button>

              {/* Instructions Section */}
              <div className={styles.section}>
                <Text weight="semibold">How it works</Text>
                <Text className={styles.instructions}>
                  Bulk import allows you to create multiple users at once from a CSV file.
                  All users will use TAP (Temporary Access Pass) authentication.
                </Text>
                <ol className={styles.stepsList}>
                  <li>Download the template above</li>
                  <li>Fill in your users (Name, User name, Mobile phone, and Block sign in required)</li>
                  <li>Configure TAP settings below (lifetime and one-time use)</li>
                  <li>Click &quot;Import CSV&quot; to upload your file</li>
                  <li>Review and confirm the import</li>
                </ol>
              </div>

              {/* Required Columns */}
              <div className={styles.section}>
                <Text weight="semibold">Required columns</Text>
                <ul className={styles.columnList}>
                  <li>
                    <strong>Name [displayName]</strong>: User&apos;s full name
                  </li>
                  <li>
                    <strong>User name [userPrincipalName]</strong>: User&apos;s email/UPN
                  </li>
                  <li>
                    <strong>Mobile phone [mobilePhone]</strong>: Phone number for TAP SMS delivery. US numbers can omit country code (e.g., 4255551234). International numbers must include country code (e.g., +442012345678).
                  </li>
                  <li>
                    <strong>Block sign in [accountEnabled]</strong>: Yes or No
                  </li>
                </ul>
              </div>

              {/* Optional Columns */}
              <div className={styles.section}>
                <Text weight="semibold">Optional columns</Text>
                <Text className={styles.instructions}>
                  First name, Last name, Job title, Department, Usage location, Street address,
                  State or province, Country or region, Office, City, ZIP or postal code,
                  Office phone
                </Text>
              </div>

              {/* TAP Configuration */}
              <div className={styles.section}>
                <Text weight="semibold">TAP Configuration (applies to all imported users)</Text>

                {/* TAP Lifetime Dropdown */}
                <div style={{ marginTop: '12px', marginBottom: '16px' }}>
                  <Label htmlFor="tap-lifetime" id="tapLifetimeLabelBulk">
                    TAP Lifetime
                  </Label>
                  <Dropdown
                    id="tap-lifetime"
                    aria-labelledby="tapLifetimeLabelBulk"
                    value={TAP_LIFETIME_OPTIONS.find(opt => opt.value === tapLifetimeMinutes)?.label || '1 hour'}
                    selectedOptions={[String(tapLifetimeMinutes)]}
                    onOptionSelect={(_ev, data) => {
                      const selectedValue = Number(data.optionValue);
                      setTapLifetimeMinutes(selectedValue);
                    }}
                    disabled={disabled}
                    data-testid="bulk-import-tap-lifetime"
                    style={{ width: 'fit-content', minWidth: '120px', marginLeft: '12px' }}
                  >
                    {TAP_LIFETIME_OPTIONS.map(option => (
                      <Option key={option.value} value={String(option.value)}>
                        {option.label}
                      </Option>
                    ))}
                  </Dropdown>
                </div>

                {/* One-time Use Checkbox */}
                <Checkbox
                  label="One-time use only"
                  checked={tapOneTimeUse}
                  onChange={(_ev, data) => setTapOneTimeUse(data.checked === true)}
                  disabled={disabled}
                  data-testid="bulk-import-tap-one-time"
                />
              </div>

              {/* Import Button */}
              <div className={styles.importButton}>
                <CSVBulkImportButton
                  disabled={disabled}
                  onCSVParsed={handleCSVParsed}
                  onError={onError}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} data-testid="bulk-import-close">
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
